import MedicamentAutocomplete from "../../../components/medicament/MedicamentAutocomplete";
import { CHRONIC_REASON_OPTIONS, chronicProductEventDescription, existingControleChronicProductIds, manualChronicProductEventDescription } from "../../../utils/chronicBilling";
import { useCallback, useEffect, useMemo, useState } from "react";
import { API } from "../../../services/api";
import { Modal, TextField } from "./AdministrationFields";
import { ADMIN_API_CONFIG } from "../model/administrationConstants";
import { formatDate, formatPatientName, productName, toDateInputValue } from "../model/administrationRules";
import {
    billingValidationBadgeStyle,
    chronicManualProductFormStyle,
    chronicProductGroupHeaderStyle,
    chronicProductGroupStyle,
    chronicProductListStyle,
    chronicProductMetaStyle,
    chronicProductRowStyle,
    disabledPrimaryButtonStyle,
    emptyStyle,
    errorStyle,
    modalFooterStyle,
    primaryButtonStyle,
    secondaryButtonStyle,
} from "../styles/administrationModalStyles";
import { inputStyle, labelStyle } from "../styles/administrationStyles";

// Traitements du patient, chargés par pages de 100.
function usePatientTreatments(patientId) {
    const [products, setProducts] = useState([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState("");

    const loadProducts = useCallback(async (nextPage = 0) => {
        if (!patientId) return;
        const setBusy = nextPage === 0 ? setLoading : setLoadingMore;
        setBusy(true);
        setError("");
        try {
            const response = await API.get(`/medicaments/patients/${patientId}/traitements?page=${nextPage}&size=100`, ADMIN_API_CONFIG);
            const data = response.data || {};
            const content = Array.isArray(data) ? data : data.content || [];
            setProducts(current => nextPage === 0 ? content : [...current, ...content]);
            setPage(data.number ?? nextPage);
            setTotalPages(data.totalPages ?? (content.length > 0 ? nextPage + 1 : nextPage));
        } catch (requestError) {
            setError(requestError.response?.data?.message || "Impossible de charger les produits du patient.");
        } finally {
            setBusy(false);
        }
    }, [patientId]);

    useEffect(() => {
        loadProducts(0);
    }, [loadProducts]);

    return { products, page, totalPages, loading, loadingMore, error, setError, loadProducts };
}

// Produits reçus le même jour (une commande), sélectionnables sauf s'ils sont déjà facturés.
function ProductGroup({ date, products, selectedIds, existingIds, onToggle }) {
    return (
        <section style={chronicProductGroupStyle}>
            <div style={chronicProductGroupHeaderStyle}>
                <strong>{date === "sans-date" ? "Commande sans date" : `Commande du ${formatDate(date)}`}</strong>
                <span>{products.length} produit{products.length > 1 ? "s" : ""}</span>
            </div>
            <div style={{ display: "grid", gap: "8px" }}>
                {products.map(product => {
                    const alreadyInHistory = existingIds.has(String(product.id));
                    return (
                        <button
                            key={product.id}
                            type="button"
                            disabled={alreadyInHistory}
                            onClick={() => onToggle(product.id)}
                            style={chronicProductRowStyle(selectedIds.has(product.id), alreadyInHistory)}
                        >
                            <span>
                                <strong>{productName(product)}</strong>
                                <small style={chronicProductMetaStyle}>
                                    {product.commandeId ? `Commande #${product.commandeId}` : "Commande non renseignée"}
                                    {product.quantite ? ` · Quantité ${product.quantite}` : ""}
                                </small>
                            </span>
                            <span style={billingValidationBadgeStyle(Boolean(product.billingValidated), alreadyInHistory)}>
                                {alreadyInHistory ? "Déjà dans l'historique" : product.billingValidated ? "Validé" : "À valider"}
                            </span>
                        </button>
                    );
                })}
            </div>
        </section>
    );
}

export function ChronicProductModal({ row, onClose, onChanged }) {
    const { products, page, totalPages, loading, loadingMore, error, setError, loadProducts } = usePatientTreatments(row?.patientId);
    const [saving, setSaving] = useState(false);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [manualProductName, setManualProductName] = useState("");
    const [manualQuantity, setManualQuantity] = useState("");
    const [manualReason, setManualReason] = useState("");

    const groupedProducts = useMemo(() => {
        const groups = new Map();
        products.forEach(product => {
            const key = toDateInputValue(product.dateReception) || "sans-date";
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(product);
        });
        return Array.from(groups.entries());
    }, [products]);

    const existingChronicProductIds = useMemo(() => existingControleChronicProductIds(row), [row]);

    const selectedProducts = useMemo(
        () => products.filter(product => selectedIds.has(product.id) && !existingChronicProductIds.has(String(product.id))),
        [existingChronicProductIds, products, selectedIds]
    );

    const toggleProduct = productId => {
        if (existingChronicProductIds.has(String(productId))) return;
        setSelectedIds(current => {
            const next = new Set(current);
            next.has(productId) ? next.delete(productId) : next.add(productId);
            return next;
        });
    };

    const addSelectedProducts = async () => {
        if (selectedProducts.length === 0) return;
        setSaving(true);
        setError("");
        try {
            await Promise.all(selectedProducts.map(product => API.post(
                `/administration/patients/${row.patientId}/billing-events`,
                {
                    type: "CHRONIC",
                    date: toDateInputValue(product.dateReception) || new Date().toISOString().slice(0, 10),
                    description: chronicProductEventDescription(product),
                },
                ADMIN_API_CONFIG
            )));
            await onChanged();
            onClose();
        } catch (requestError) {
            setError(requestError.response?.data?.message || "Impossible d'ajouter ces produits.");
        } finally {
            setSaving(false);
        }
    };

    const addManualProduct = async event => {
        event.preventDefault();
        const productNameValue = manualProductName.trim();
        if (!productNameValue) return;
        setSaving(true);
        setError("");
        try {
            await API.post(
                `/administration/patients/${row.patientId}/billing-events`,
                {
                    type: "CHRONIC",
                    date: new Date().toISOString().slice(0, 10),
                    description: manualChronicProductEventDescription({
                        name: productNameValue,
                        quantity: manualQuantity,
                        reason: manualReason,
                    }),
                },
                ADMIN_API_CONFIG
            );
            await onChanged();
            onClose();
        } catch (requestError) {
            setError(requestError.response?.data?.message || "Impossible d'ajouter ce produit manuel.");
        } finally {
            setSaving(false);
        }
    };

    const hasMore = page + 1 < totalPages;

    return (
        <Modal title={`Ajouter un produit — ${formatPatientName(row)}`} onClose={onClose} width={780}>
            <form onSubmit={addManualProduct} style={chronicManualProductFormStyle}>
                <label style={labelStyle}>
                    Nom du produit
                    <MedicamentAutocomplete
                        value={manualProductName}
                        onChange={setManualProductName}
                        inputStyle={inputStyle}
                    />
                </label>
                <label style={labelStyle}>
                    Quantité
                    <TextField value={manualQuantity} onChange={setManualQuantity} />
                </label>
                <label style={labelStyle}>
                    <span>Motif</span>
                    <select value={manualReason} onChange={event => setManualReason(event.target.value)} style={inputStyle}>
                        <option value="">—</option>
                        {CHRONIC_REASON_OPTIONS.filter(Boolean).map(reason => (
                            <option key={reason} value={reason}>{reason}</option>
                        ))}
                    </select>
                </label>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button type="submit" disabled={saving || !manualProductName.trim()} style={saving || !manualProductName.trim() ? disabledPrimaryButtonStyle : primaryButtonStyle}>
                        {saving ? "Ajout…" : "Ajouter le produit"}
                    </button>
                </div>
            </form>
            {loading ? (
                <p style={emptyStyle}>Chargement…</p>
            ) : products.length === 0 ? (
                <p style={emptyStyle}>Aucun produit rattaché à ce patient.</p>
            ) : (
                <div style={chronicProductListStyle}>
                    {groupedProducts.map(([date, dateProducts]) => (
                        <ProductGroup
                            key={date}
                            date={date}
                            products={dateProducts}
                            selectedIds={selectedIds}
                            existingIds={existingChronicProductIds}
                            onToggle={toggleProduct}
                        />
                    ))}
                    {hasMore && (
                        <button type="button" onClick={() => loadProducts(page + 1)} disabled={loadingMore} style={secondaryButtonStyle}>
                            {loadingMore ? "Chargement…" : "Charger plus de produits"}
                        </button>
                    )}
                </div>
            )}
            {error && <p style={errorStyle}>{error}</p>}
            <div style={modalFooterStyle}>
                <button type="button" onClick={onClose} disabled={saving} style={secondaryButtonStyle}>Annuler</button>
                <button
                    type="button"
                    onClick={addSelectedProducts}
                    disabled={saving || selectedProducts.length === 0}
                    style={saving || selectedProducts.length === 0 ? disabledPrimaryButtonStyle : primaryButtonStyle}
                >
                    {saving ? "Ajout…" : "Ajouter ces produits"}
                </button>
            </div>
        </Modal>
    );
}
